import { useCallback, useEffect, useRef, useState } from 'react';
import { inSalishSea } from '@whaletail/shared';

export interface MyLocation {
  lat: number;
  lon: number;
  /** Radius of uncertainty, in metres. */
  accuracy: number;
}

export type GeoStatus = 'off' | 'locating' | 'on' | 'denied' | 'unavailable' | 'insecure' | 'error';

export interface GeoState {
  status: GeoStatus;
  position: MyLocation | null;
  /** A short human sentence for the current problem, or null. */
  message: string | null;
  /** True exactly once per enable, when the first fix arrives — the moment to recentre the map. */
  firstFix: boolean;
}

const MESSAGES: Partial<Record<GeoStatus, string>> = {
  denied: 'Location access was blocked. Allow it for this site in your browser settings to show where you are.',
  unavailable: "Your device couldn't work out its position. Try again outside, or with location services on.",
  insecure: 'Showing your location needs a secure connection (HTTPS). It works on localhost and through the tunnel.',
  error: "Couldn't get your location.",
};

/**
 * "You are here", on request.
 *
 * Nothing runs until the person turns it on, so the browser's permission prompt
 * appears in response to a click rather than on page load. The position is kept
 * in this hook's state and drawn on the map; it is never sent to the server.
 */
export function useGeolocation() {
  const [state, setState] = useState<GeoState>({ status: 'off', position: null, message: null, firstFix: false });
  const watchId = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setState({ status: 'off', position: null, message: null, firstFix: false });
  }, []);

  const start = useCallback(() => {
    if (watchId.current !== null) return;
    if (!('geolocation' in navigator)) {
      setState({ status: 'unavailable', position: null, message: MESSAGES.unavailable ?? null, firstFix: false });
      return;
    }
    // Browsers only expose geolocation to secure contexts (HTTPS or localhost),
    // and a plain-HTTP LAN address fails with an opaque error, so say why.
    if (!window.isSecureContext) {
      setState({ status: 'insecure', position: null, message: MESSAGES.insecure ?? null, firstFix: false });
      return;
    }

    setState({ status: 'locating', position: null, message: null, firstFix: false });
    let gotFirst = false;

    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const first = !gotFirst;
        gotFirst = true;
        setState({
          status: 'on',
          position: { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy },
          message: null,
          firstFix: first,
        });
      },
      (err) => {
        // PERMISSION_DENIED = 1, POSITION_UNAVAILABLE = 2, TIMEOUT = 3
        const status: GeoStatus = err.code === 1 ? 'denied' : err.code === 2 ? 'unavailable' : 'error';
        if (err.code === 1 && watchId.current !== null) {
          navigator.geolocation.clearWatch(watchId.current);
          watchId.current = null;
        }
        // A timeout on a later fix should not throw away a position we already have.
        setState((prev) =>
          err.code === 3 && prev.position
            ? prev
            : { status, position: null, message: MESSAGES[status] ?? null, firstFix: false },
        );
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 15_000 },
    );
  }, []);

  useEffect(
    () => () => {
      if (watchId.current !== null) navigator.geolocation?.clearWatch(watchId.current);
    },
    [],
  );

  return { ...state, start, stop, toggle: state.status === 'off' ? start : stop };
}

/** Whether a fix falls inside the area this map covers. */
export const isInMapArea = (p: MyLocation): boolean => inSalishSea(p.lat, p.lon);

/**
 * A polygon approximating a circle of `radiusM` metres, for drawing the accuracy
 * ring on the map. MapLibre circle layers size in pixels, not metres, so a real
 * geographic ring needs real coordinates.
 */
export function circlePolygon(lat: number, lon: number, radiusM: number, steps = 48): GeoJSON.Polygon {
  const coords: Array<[number, number]> = [];
  const dLat = radiusM / 111_320;
  const dLon = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    coords.push([lon + dLon * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return { type: 'Polygon', coordinates: [coords] };
}
